import { Component, ReactNode, forwardRef } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import type { ChatMessage } from '../../services/chatTypes';
import './MessageBubble.css';

interface MessageBubbleProps {
  message: ChatMessage;
  index?: number;
}

interface MarkdownErrorBoundaryState {
  hasError: boolean;
}

class MarkdownErrorBoundary extends Component<
  { children: ReactNode; fallback: string },
  MarkdownErrorBoundaryState
> {
  constructor(props: { children: ReactNode; fallback: string }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): MarkdownErrorBoundaryState {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return <span className="message-bubble-raw-text">{this.props.fallback}</span>;
    }
    return this.props.children;
  }
}

export const MessageBubble = forwardRef<HTMLDivElement, MessageBubbleProps>(
  function MessageBubble({ message, index }, ref) {
    const isUser = message.role === 'user';

    return (
      <div
        ref={ref}
        className={`message-bubble ${isUser ? 'message-bubble--user' : 'message-bubble--assistant'}`}
        tabIndex={-1}
        data-message-index={index}
        aria-label={`${isUser ? 'You' : 'Assistant'}: ${message.content.slice(0, 100)}${message.content.length > 100 ? '...' : ''}`}
      >
        <div className="message-bubble__content">
          {isUser ? (
            <p className="message-bubble__text">{message.content}</p>
          ) : (
            <MarkdownErrorBoundary fallback={message.content}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeSanitize]}
                components={{
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {children}
                    </a>
                  ),
                }}
              >
                {message.content}
              </ReactMarkdown>
            </MarkdownErrorBoundary>
          )}
        </div>
      </div>
    );
  }
);
